import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Modal } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import { IoMdCopy } from 'react-icons/io';
import { AiOutlineFileWord } from 'react-icons/ai';
import { MdRefresh } from 'react-icons/md';

import { docxExport } from './DisasterGPTDataHelpers/DGPTExport';

import DataSources from 'components/DisasterGPT/DataSources';
import AgentModeSelector from 'components/DisasterGPT/AgentModeSelector';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import AnnotationRenderer from './AnnotationRenderer';
import ActionResultCard from './ActionResultCard';
import SlashCommandMenu from './SlashCommandMenu';
import { useFetchCitationFileUrl } from './hooks/useFetchCitationFileUrl';
import { useAICommands } from './hooks/useAICommands';
import { expandSlashCommand } from './utils/expandSlashCommand';

import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

import {
  setSelectedDChat,
  setCurrentSitrepId,
  cancelRun,
  resumeDChat,
  setStatus,
} from 'slices/dchatSlice';
import ReviewQueueModal from './agentConfirm/ReviewQueueModal';

import {
  usePollDChat,
  useStopPollDChat,
  usePostDChat,
  useDeleteDisasterChat,
} from './hooks/useDisasterChats';
import { selectIncident, useAppSelector } from 'slices/commonSelectors';
import {
  prefetchLayerFeaturesForAI,
  withLegendClassFiltersForAI,
} from './utils/prefetchLayerFeaturesForAI';
import { compactDisasterChatPayload } from './utils/layerSnapshotCompact';
import { withArcGisUserTokensForAI } from './utils/arcGisUserTokenForAI';
import {
  applyDrawnAreaToLayersSelection,
  upsertSelectedLayerForAI,
} from './utils/addSelectedLayerForAI';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useIsCommandCenter } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/isCommandCenter';
import { toast } from 'react-toastify';
import { useHighSecurityFlag } from 'utils/featureFlags';
import { defaultHubRealtimeSelections } from './chatV2/dataSourceSelections';

import './ChatDisasterGPT.css';
import './SitrepSection.css';

// Shared with the V2 drawer (chatV2/useChatSession) so both shells expand
// slash commands identically.
export { expandSlashCommand };

const ChatDisasterGPT = ({ toggle, selectedDatetime }) => {
  const reduxDispatch = useDispatch();

  const [input, setInput] = useState('');
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sendMessageTimestamp, setSendMessageTimestamp] = useState();
  const [realtimeSelections, setRealtimeSelections] = useState([]);
  const [priorStatus, setPriorStatus] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMode, setSelectedMode] = useState('agent_confirm');
  const hubDefaultsSeededRef = useRef(false);
  const isHub = useIsCommandCenter();
  const highSecurity = useHighSecurityFlag();
  const incident = useAppSelector(selectIncident);

  const dchatStatus = useSelector((state) => state.dchat.status);
  const dchat = useSelector((state) => state.dchat.dchat);
  const streamtext = useSelector((state) => state.dchat.streamtext);

  // Single source of truth for "the agent is actively working". Used for the
  // input/progress swap, the disabled states, and the Cancel button so they can
  // never disagree and visibly thrash as the polled status churns.
  const isAgentRunning = dchatStatus === 'loading' || dchatStatus === 'ongoing';

  const pollDChat = usePollDChat();
  const stopPollDChat = useStopPollDChat();
  const postDChat = usePostDChat();
  const deleteDisasterChat = useDeleteDisasterChat();
  const handleFetchFileUrl = useFetchCitationFileUrl();

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Slash-command palette source. The palette (SlashCommandMenu) owns its own
  // search box, collapsible categories, and keyboard navigation — here we just
  // supply the full curated command list and the open/close + select wiring.
  // Picking a command prefills an editable prompt; it does not invoke a tool
  // directly — the agent still runs normally. Mode-aware: guided skills are
  // hidden in read-only 'ask' mode (the backend skips the skill layer there).
  const allCommands = useAICommands(selectedMode);

  // Derive SharePoint and tenant values from incident prop
  const sharepointDriveId = useMemo(
    () => incident?.sharepoint_location,
    [incident]
  );
  const tenantId = useMemo(() => incident?.tenant_id, [incident]);
  const mapCtx = useOptionalUscgMapContext();
  const {
    externalLayerTimeFilterById = {},
    getAddedExternalLayers,
    legendClassFilterByLayerId,
    aiLayerPickMode = false,
    setAiLayerPickMode,
    registerAiLayerPickHandler,
    registerAiDrawnAreaHandler,
    getVisibleLayersForAI,
  } = mapCtx ?? {};

  useEffect(() => {
    if (!registerAiLayerPickHandler) return undefined;
    registerAiLayerPickHandler((payload) => {
      setRealtimeSelections((prev) => {
        const { next, added } = upsertSelectedLayerForAI(
          prev,
          payload,
          externalLayerTimeFilterById
        );
        if (added) {
          const filterNote = payload.spatialFilterLabel
            ? ` (${payload.spatialFilterLabel})`
            : payload.legendFilterLabel
              ? ` (${payload.legendFilterLabel})`
              : '';
          toast.success(`Added ${payload.name}${filterNote} to Layers`);
        } else {
          toast.info(`${payload.name} is already selected`);
        }
        return next;
      });
    });
    return () => registerAiLayerPickHandler(null);
  }, [registerAiLayerPickHandler, externalLayerTimeFilterById]);

  useEffect(() => {
    if (!registerAiDrawnAreaHandler) return undefined;
    registerAiDrawnAreaHandler((payload) => {
      setRealtimeSelections((prev) => {
        const { next, added } = applyDrawnAreaToLayersSelection(
          prev,
          payload,
          getVisibleLayersForAI(),
          externalLayerTimeFilterById
        );
        if (added) {
          toast.success('Added map area to Layers');
        } else {
          toast.info('Turn on a map layer to scope a drawn area');
        }
        return next;
      });
    });
    return () => registerAiDrawnAreaHandler(null);
  }, [
    registerAiDrawnAreaHandler,
    externalLayerTimeFilterById,
    getVisibleLayersForAI,
  ]);

  useEffect(() => {
    if (!aiLayerPickMode) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setAiLayerPickMode(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [aiLayerPickMode, setAiLayerPickMode]);

  const toggleAiLayerPickMode = useCallback(() => {
    if (!setAiLayerPickMode) return;
    const next = !aiLayerPickMode;
    setAiLayerPickMode(next);
    if (next) {
      toast.info('Click a layer, legend class, map polygon, or draw an area');
    }
  }, [aiLayerPickMode, setAiLayerPickMode]);

  // Simplified sendMessage function
  const sendMessage = useCallback(
    async (message, newChatName = '', prePromptData = null) => {
      let data = prePromptData !== null ? prePromptData : realtimeSelections;

      // Attach legend class filters early (no snapshot yet) for optimistic UI + POST.
      const layersSelection = Array.isArray(data)
        ? data.find((s) => s?.source === 'Layers')
        : null;
      if (
        layersSelection &&
        Array.isArray(layersSelection.selectedLayers) &&
        layersSelection.selectedLayers.length > 0
      ) {
        data = data.map((sel) =>
          sel.source === 'Layers'
            ? {
                ...sel,
                selectedLayers: withLegendClassFiltersForAI(
                  sel.selectedLayers,
                  legendClassFilterByLayerId
                ),
              }
            : sel
        );
      }

      let finalNewChatName = newChatName;
      if (!!newChatName || !newChatName.length) {
        finalNewChatName = message.slice(0, 50);
      }

      // Immediate feedback: do not wait for layer prefetch before showing
      // processing state (prefetch can take seconds for ArcGIS pages).
      reduxDispatch(setStatus('loading'));
      setPriorStatus('Loading layer data…');
      const optimisticHistorySelections = compactDisasterChatPayload({
        realtimeSelections: data,
        messages: [],
      }).historyRealtimeSelections;
      const sendTimestamp = new Date();
      const priorMessages = messages;
      setMessages([
        ...priorMessages,
        {
          role: 'USER',
          content: message,
          timestamp: sendTimestamp,
          realtimeSelections: optimisticHistorySelections,
          selectedDatetime: selectedDatetime,
        },
      ]);
      setInput('');
      setSendMessageTimestamp(sendTimestamp);

      // Prefetch map layer features so ArcGIS userAuth layers work without a
      // server portal token; also speeds internal layer reads for the agent.
      try {
        const layersSel = Array.isArray(data)
          ? data.find((s) => s?.source === 'Layers')
          : null;
        if (
          layersSel &&
          Array.isArray(layersSel.selectedLayers) &&
          layersSel.selectedLayers.length > 0
        ) {
          const withSnapshots = await prefetchLayerFeaturesForAI(
            layersSel.selectedLayers,
            {
              timeFiltersByLayerId: externalLayerTimeFilterById,
              getAddedExternalLayers,
            }
          );
          // Ephemeral user token for COMS ArcGIS queries (userAuth / Token Required).
          // Oversized sample snapshots are dropped when a token is present.
          const withTokens = withArcGisUserTokensForAI(withSnapshots, {
            getAddedExternalLayers,
          });
          data = data.map((sel) =>
            sel.source === 'Layers'
              ? { ...sel, selectedLayers: withTokens }
              : sel
          );
        }
      } catch (error) {
        console.warn('[ChatDisasterGPT] Layer prefetch failed:', error);
      }

      // Compact full POST: drop history snapshots + budget current-turn attributes
      // so we stay under COMS bodyParser 1mb (history replay was causing residual 413s).
      const compacted = compactDisasterChatPayload({
        realtimeSelections: data,
        messages: priorMessages,
      });

      postDChat({
        dchatId: dchat.id,
        message: message,
        realtimeSelections: compacted.realtimeSelections,
        selectedDatetime: selectedDatetime,
        newChatName: finalNewChatName,
        tenantId: tenantId,
        sharepointDriveId: sharepointDriveId,
        messages: compacted.messages,
        mode: selectedMode,
      });
    },
    [
      realtimeSelections,
      dchat.id,
      selectedDatetime,
      tenantId,
      sharepointDriveId,
      messages,
      postDChat,
      setMessages,
      setInput,
      setSendMessageTimestamp,
      selectedMode,
      externalLayerTimeFilterById,
      getAddedExternalLayers,
      legendClassFilterByLayerId,
      reduxDispatch,
      dchatStatus,
    ]
  );

  const areSelectionsValid = useCallback(() => {
    if (realtimeSelections.length) {
      const sitrepSelection = realtimeSelections.find(
        (s) => s.source === 'SITREP'
      );
      if (!!sitrepSelection && !sitrepSelection.id) {
        return false;
      }
      const layersSelection = realtimeSelections.find(
        (s) => s.source === 'Layers'
      );
      if (
        !!layersSelection &&
        (!Array.isArray(layersSelection.selectedLayers) ||
          layersSelection.selectedLayers.length === 0)
      ) {
        return false;
      }
    }
    return true;
  }, [realtimeSelections]);

  // Trigger button: open/close the command palette.
  const toggleCommandMenu = useCallback(() => {
    setSlashMenuOpen((open) => !open);
  }, []);

  const closeCommandMenu = useCallback(() => {
    setSlashMenuOpen(false);
    inputRef.current?.focus();
  }, []);

  const selectSlashCommand = useCallback((cmd) => {
    // Insert the literal command token (e.g. "/create-objective ") so it's clear which command
    // is active. It is expanded back to the natural-language prompt at send time (submitMessage).
    setInput(`${cmd.command} `);
    setSlashMenuOpen(false);
    // Refocus the textarea with the cursor at the end of the inserted token.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }
    });
  }, []);

  // Expand a leading slash-command literal back to its prompt template before sending, so the
  // user sees a clean command but the agent gets a well-formed instruction. Any text the user
  // typed after the command is preserved. Non-command input is sent unchanged.
  const submitMessage = useCallback(() => {
    if (!areSelectionsValid() || input.length === 0) {
      return;
    }
    sendMessage(expandSlashCommand(input, allCommands));
  }, [input, allCommands, sendMessage, areSelectionsValid]);

  const handleInputKeyDown = useCallback(
    (e) => {
      // Typing "/" as the first character opens the command palette instead of
      // inserting a slash (the palette then owns search + selection). The "/"
      // button does the same thing via a click.
      if (e.key === '/' && input.length === 0) {
        e.preventDefault();
        setSlashMenuOpen(true);
        return;
      }

      // Default behavior: Enter sends (Shift+Enter inserts a newline).
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submitMessage();
      }
    },
    [input, submitMessage]
  );

  // Keep the mode selector in sync with the loaded chat's persisted mode, so
  // reopening a chat (or reloading the page) doesn't silently show the default.
  useEffect(() => {
    if (dchat?.id && dchat.mode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedMode(dchat.mode);
    }
  }, [dchat?.id, dchat?.mode]);

  useEffect(() => {
    // Resume polling only when opening/selecting a chat that is already
    // in-flight on the server (e.g. page refresh). Do NOT start polling when
    // local redux status flips to `loading` during layer prefetch — that race
    // polls a still-Complete chat, sets status back to succeeded, and clears
    // the spinner for the entire prefetch window. postDChat/resumeDChat start
    // their own polling after the POST.
    if (!dchat?.id) {
      return undefined;
    }
    const serverInProgress =
      dchat.status === 'Planning' ||
      dchat.status === 'Gathering Data' ||
      dchat.status === 'Executing Tools';
    if (serverInProgress) {
      pollDChat(dchat.id);
    }

    return () => {
      stopPollDChat();
    };
    // Only re-bind when the selected chat changes. Status-driven restarts were
    // thrashing the poll interval (loading ↔ ongoing) and clearing prefetch UI.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [dchat?.id, pollDChat, stopPollDChat]);

  // Track previous status to display during loading states
  // This intentionally reads and updates priorStatus to track state transitions
  useEffect(() => {
    if (dchat) {
      if (dchat.status !== 'Complete' && dchat.status !== 'Error') {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPriorStatus(dchat.status);
      } else if (
        (dchat.status === 'Complete' && priorStatus !== 'Complete') ||
        (dchat.status === 'Error' && priorStatus !== 'Error')
      ) {
        setPriorStatus('Initializing');
      } else {
        setPriorStatus(dchat.status);
      }
    }
  }, [dchat, priorStatus]);

  // Synchronize Redux dchat state with local component state
  // This is intentional: local state enables optimistic updates in sendMessage/handleRedoMessage
  // while this effect syncs with server state when dchat updates
  useEffect(() => {
    if (dchat?.messages) {
      let showMsg = [...dchat.messages];
      // Keep the agent's streamed narration visible while a leg is in progress AND while the approval modal is up
      // (pending_confirmation) — otherwise the narration flickers away each time the agent pauses for approval.
      if (
        streamtext &&
        (dchatStatus === 'loading' ||
          dchatStatus === 'ongoing' ||
          dchatStatus === 'pending_confirmation')
      ) {
        const streamMsg = {
          role: 'ASSISTANT',
          content: streamtext,
        };
        showMsg = [...showMsg, streamMsg];
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMessages(showMsg);
    }
    if (!dchat || Object.keys(dchat).length === 0) {
      setMessages([]);
      hubDefaultsSeededRef.current = false;
      if (isHub) {
        setRealtimeSelections(defaultHubRealtimeSelections(highSecurity));
        hubDefaultsSeededRef.current = true;
      }
    }

    if (dchat?.messages?.length) {
      const lastMessage = dchat.messages[dchat.messages.length - 1];
      const rsel = lastMessage.realtimeSelections || [];
      setRealtimeSelections(rsel);
      hubDefaultsSeededRef.current = true;
    }
  }, [dchat, streamtext, dchatStatus, isHub, highSecurity]);

  useEffect(() => {
    if (!isHub) return;
    const hasMessages =
      messages.length > 0 || (dchat?.messages?.length ?? 0) > 0;
    if (hasMessages) {
      hubDefaultsSeededRef.current = true;
      return;
    }
    if (hubDefaultsSeededRef.current) return;
    if (realtimeSelections.length > 0) {
      hubDefaultsSeededRef.current = true;
      return;
    }
    setRealtimeSelections(defaultHubRealtimeSelections(highSecurity));
    hubDefaultsSeededRef.current = true;
  }, [
    isHub,
    highSecurity,
    messages.length,
    dchat?.messages?.length,
    realtimeSelections.length,
  ]);

  useEffect(() => {
    if (messagesEndRef.current && dchatStatus === 'succeeded') {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [dchatStatus]);

  function cancelRunClicked() {
    reduxDispatch(cancelRun(dchat));
  }

  // HITL: the agent is paused on one or more proposed writes awaiting the
  // user's approve/edit/reject decision (see AgentConfirmationModal).
  const pendingConfirmations =
    dchat?.status === 'PendingConfirmation' &&
    Array.isArray(dchat?.pending_confirmation)
      ? dchat.pending_confirmation
      : [];

  // The agent's "ask" for this batch is its streamed narration (the same text shown live in the chat). Surface it in
  // the modal too so the chat and the approval surface present the identical ask.
  const confirmationAskText =
    pendingConfirmations.length > 0 && streamtext ? streamtext : undefined;

  // Returns false when the resume request fails so ReviewQueueModal can leave
  // its "applying" spinner and let the reviewer retry.
  function handleConfirmationSubmit(decisions) {
    return reduxDispatch(resumeDChat(dchat.id, decisions));
  }

  function deleteSessionClicked() {
    if (dchat.id) {
      setShowDeleteModal(true);
    }
  }

  function closeClicked() {
    reduxDispatch(setSelectedDChat({}));
    reduxDispatch(setCurrentSitrepId());
    toggle();
  }

  function handleConfirmDelete() {
    deleteDisasterChat.mutate(dchat.id);
    closeClicked();
    setShowDeleteModal(false);
  }

  function handleCancelDelete() {
    setShowDeleteModal(false);
  }

  const onUseBlueprint = useCallback(
    (blueprint) => {
      const structuredBlueprint = `# ${blueprint.title}\n\n${blueprint.description}`;
      let promptData = realtimeSelections;

      // Set realtimeSelections to the blueprint's datasources
      if (blueprint.settings?.datasources) {
        setRealtimeSelections(blueprint.settings.datasources);
        promptData = blueprint.settings.datasources;
      }

      // Send message with updated data source selections
      sendMessage(structuredBlueprint, blueprint.title, promptData);
    },
    [realtimeSelections, sendMessage]
  );

  // Auto-send initial blueprint message when a new chat with a blueprint is created
  // This is an intentional side effect that triggers on mount/dchat change
  useEffect(() => {
    if (dchat && dchat.initialBlueprint && !dchat.id && !messages.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      onUseBlueprint(dchat.initialBlueprint);
    }
  }, [dchat, messages, onUseBlueprint]);

  const markdownComponents = {
    table({ ...props }) {
      return <table className="custom-markdown-table" {...props} />;
    },
    th({ ...props }) {
      return <th className="custom-markdown-th" {...props} />;
    },
    td({ ...props }) {
      return <td className="custom-markdown-td" {...props} />;
    },
    tr({ ...props }) {
      return <tr className="custom-markdown-tr" {...props} />;
    },
  };

  const customSchema = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      span: [...(defaultSchema.attributes.span || []), 'style'],
    },
  };

  const handleRedoMessage = (messageIndex) => {
    const userMessage = messages[messageIndex - 1];
    if (!userMessage || userMessage.role !== 'USER') {
      return;
    }

    const newMessages = messages.slice(0, messageIndex - 1);
    setMessages(newMessages);

    postDChat({
      dchatId: dchat.id,
      message: userMessage.content,
      realtimeSelections: userMessage.realtimeSelections,
      selectedDatetime: selectedDatetime,
      newChatName: '',
      tenantId: tenantId,
      sharepointDriveId: sharepointDriveId,
      messages: newMessages,
      mode: selectedMode,
    });

    setMessages([
      ...newMessages,
      {
        role: 'USER',
        content: userMessage.content,
        timestamp: new Date(),
        realtimeSelections: userMessage.realtimeSelections,
        selectedDatetime: selectedDatetime,
      },
    ]);
    setSendMessageTimestamp(new Date());
  };

  return (
    <div
      className="d-flex flex-column"
      style={{ height: '100%', maxHeight: '100%' }}
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          padding: '1rem',
          minHeight: 0,
        }}
      >
        <label>
          PRATUS AI generated content may be incorrect. Always check important
          information. For bug reports and feature requests, please contact{' '}
          <a href="mailto:support@disastertech.com">support@disastertech.com</a>
        </label>
        <hr />
        <div>
          {messages.map((message, index) => {
            let timestamp;

            if (dchat?.id) {
              timestamp = message.timestamp;
            } else {
              timestamp = sendMessageTimestamp;
            }
            if (timestamp) {
              timestamp = dayjs(timestamp).format('YYYY-MM-DD HH:mm');
            } else {
              timestamp = '(No datetime provided)';
            }

            const messageContent = message?.content.replace(
              /\*\*\[[^\]]*\]\*\*|【[^】]*】/g,
              ''
            );

            const messageRole = message.role.toUpperCase();
            const displayRole =
              messageRole === 'ASSISTANT' ? 'PRATUS AI' : messageRole;

            return (
              <div key={'dpgt-message-' + index} id={'dpgt-message-' + index}>
                <div className="d-flex align-items-center gap-2">
                  <strong>{displayRole}:</strong>
                  {messageRole === 'ASSISTANT' && (
                    <MdRefresh
                      className="redo-icon"
                      onClick={() => handleRedoMessage(index)}
                      title="Redo this response"
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                  <IoMdCopy
                    className="clipboard-icon"
                    onClick={() =>
                      docxExport(
                        messageContent,
                        'Clipboard',
                        dchat.name || 'PRATUS AI Chat'
                      )
                    }
                    title="Copy to clipboard"
                    style={{ cursor: 'pointer' }}
                  />
                  <AiOutlineFileWord
                    className="download-icon"
                    onClick={() =>
                      docxExport(
                        [message],
                        'Download',
                        dchat.name || 'PRATUS AI Chat'
                      )
                    }
                    title="Click to Download"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                <div>
                  <label>{timestamp}</label>
                  <div className="EventAIAnalysis-paragraph">
                    {messageRole === 'ASSISTANT' &&
                    message.annotations?.length > 0 ? (
                      <AnnotationRenderer
                        content={messageContent}
                        annotations={message.annotations}
                        onFetchFileUrl={handleFetchFileUrl}
                        messageIndex={index}
                      />
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[
                          [rehypeRaw, { passThrough: ['element'] }],
                          [rehypeSanitize, customSchema],
                        ]}
                        components={{
                          ...markdownComponents,
                          a: ({ _, ...linkProps }) => {
                            return (
                              <a
                                {...linkProps}
                                target="_blank"
                                rel="noopener noreferrer"
                              />
                            );
                          },
                        }}
                      >
                        {messageContent}
                      </ReactMarkdown>
                    )}
                    {messageRole === 'ASSISTANT' && (
                      <ActionResultCard actions={message.actions} />
                    )}
                  </div>
                </div>
                <hr />
              </div>
            );
          })}
        </div>
        <div ref={messagesEndRef} />
      </div>

      <div className="ChatDisasterGPT-input-area">
        <Row>
          {isAgentRunning ? (
            <div>
              <h5 className="mb-1">
                {priorStatus}&nbsp;<i className="fa fa-spinner fa-pulse"></i>
              </h5>
              <div>
                <span>
                  PRATUS AI Chat on average is 10-60 seconds but depending on
                  the volume of data can take longer to generate an answer.
                </span>
              </div>
            </div>
          ) : (
            <div className="ChatDisasterGPT-input-wrap position-relative">
              {slashMenuOpen && allCommands.length > 0 && (
                <SlashCommandMenu
                  commands={allCommands}
                  onSelect={selectSlashCommand}
                  onClose={closeCommandMenu}
                />
              )}
              <StylishNewTextArea
                ref={inputRef}
                type="text"
                value={input}
                disabled={isAgentRunning}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={'Message PRATUS AI Chat...  (type / for commands)'}
              />
              <div>
                <div className="ChatDisasterGPT-input-button-wrap">
                  <div className="d-flex justify-content-between align-items-center ChatDisasterGPT-pill-row">
                    <div className="d-flex align-items-center gap-2">
                      <StylishNewButton
                        id="dgpt-command-trigger"
                        className="button--secondary button--icon"
                        onClick={toggleCommandMenu}
                        disabled={isAgentRunning}
                        title="Browse commands"
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            lineHeight: 1,
                          }}
                        >
                          /
                        </span>
                      </StylishNewButton>
                      <StylishNewButton
                        className={`button--secondary button--icon${
                          aiLayerPickMode ? ' active' : ''
                        }`}
                        onClick={toggleAiLayerPickMode}
                        disabled={isAgentRunning}
                        title="Pick a layer, legend class, map polygon, or drawn area for AI"
                        aria-label="Pick a layer, legend class, map polygon, or drawn area for AI"
                        aria-pressed={aiLayerPickMode}
                      >
                        <SharedIcon iconName="arrow_selector_tool" size="sm" />
                      </StylishNewButton>
                      <DataSources
                        selections={realtimeSelections}
                        setSelections={setRealtimeSelections}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <AgentModeSelector
                        selectedMode={selectedMode}
                        onModeChange={setSelectedMode}
                        disabled={isAgentRunning}
                      />
                      <StylishNewButton
                        onClick={submitMessage}
                        disabled={!areSelectionsValid() || input.length === 0}
                        className="button--primary"
                      >
                        Send
                      </StylishNewButton>
                    </div>
                  </div>
                </div>
                {!areSelectionsValid() && (
                  <span>
                    Choose a PRATUS AI SITREP to send to PRATUS AI Chat
                  </span>
                )}
              </div>
            </div>
          )}
        </Row>
      </div>

      <div className="border-top position-relative p-3">
        {/* Cancel lives in its own absolutely-positioned slot so showing/hiding
            it never shifts or re-centers the export icon row. */}
        {isAgentRunning && !!dchat && (
          <div
            className="position-absolute"
            style={{ left: '1rem', top: '50%', transform: 'translateY(-50%)' }}
          >
            <StylishNewButton
              className="button--secondary button--icon"
              type="button"
              onClick={cancelRunClicked}
              title="Cancel Current Run"
            >
              <SharedIcon iconName={'cancel'} size={'sm'} />
            </StylishNewButton>
          </div>
        )}

        <div className="d-flex justify-content-center gap-3">
          {!!dchat && !!dchat.id && (
            <>
              <StylishNewButton
                className="button--secondary button--icon"
                type="button"
                onClick={() =>
                  docxExport(
                    messages,
                    'Download',
                    dchat.name || 'PRATUS AI Chat'
                  )
                }
                title="Download Docx"
              >
                <SharedIcon iconName={'download'} size={'sm'} />
              </StylishNewButton>

              <StylishNewButton
                className="button--secondary button--icon"
                type="button"
                onClick={() =>
                  docxExport(
                    messages,
                    'Clipboard',
                    dchat.name || 'PRATUS AI Chat'
                  )
                }
                title="Copy to Clipboard"
              >
                <SharedIcon iconName={'content_copy'} size={'sm'} />
              </StylishNewButton>

              <StylishNewButton
                className="button--secondary button--icon"
                type="button"
                onClick={deleteSessionClicked}
                title="Delete Session"
              >
                <SharedIcon iconName={'delete'} size={'sm'} />
              </StylishNewButton>
            </>
          )}
        </div>
      </div>

      <ReviewQueueModal
        show={pendingConfirmations.length > 0}
        confirmations={pendingConfirmations}
        askText={confirmationAskText}
        submitting={dchatStatus === 'loading'}
        onSubmit={handleConfirmationSubmit}
        onClose={toggle}
      />

      <Modal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        centered
        backdrop={true}
        keyboard={true}
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Delete Session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this PRATUS AI Chat session?
        </Modal.Body>
        <Modal.Footer>
          <StylishNewButton
            className="button--secondary"
            onClick={handleCancelDelete}
          >
            Cancel
          </StylishNewButton>
          <StylishNewButton
            className="button--tertiary"
            onClick={handleConfirmDelete}
          >
            Delete
          </StylishNewButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChatDisasterGPT;
